import { SecureDocModel } from "../../models/secure-docs.model";
import { useEffect, useRef, useState } from "react";
import setToast from "../../helpers/toaster.helper";
import { ToasterType } from "../shared/toaster/toaster.model";
import { FalkonDocMobileBackIcon, FalkonDocUploadIcon, FalkonDocUploadSuccessIcon, UploadDocsIcon } from "../../icon";
import AddRecipientComponent from "./add-recipients-component";
import DragAndDropComponent from "./drag-n-drop.component";
import ButtonComponent from "../shared/button/button.component";
import { useSelector } from "react-redux";
import * as selector from "../../selectors/selectors";
import { IconButton } from "@mui/material";
import { AppRoutes } from "../routes/route.path";
import { useNavigate } from "react-router-dom";

interface Props {
    secureDocsData: SecureDocModel[];
    isTeams: boolean;
    isUpdateMode?: boolean;
    fileID?: string;
}

export interface RecipientFormData {
    recipient_id: string;
    email: string;
    order_number: number;
    name?: string;
    phone?: string;
    type?: string;
    is_completed?: boolean;
    is_invite_sent?: boolean;
}

export interface PreservedFormData {
    messageText?: string;
    reminderDays?: number;
    passwordProtect?: boolean;
    password?: string;
    completeInOrder?: boolean;
}

interface StepData {
    recipientEmails: RecipientFormData[];
    newFileID: string;
    selectedFile: File | null;
    uploadedFileData: any;
    loadedRecipients: RecipientFormData[];
    messageText?: string;
    reminderDays?: number;
    passwordProtect?: boolean;
    password?: string;
    completeInOrder?: boolean;
}

const StepperUpload: React.FC<Props> = (props) => {
    const [activeStep, setActiveStep] = useState(props.isUpdateMode ? 2 : 1);
    const [isDragOver, setIsDragOver] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const SecureDocs: SecureDocModel[] = useSelector(selector.secureDocs);
    const [dragError, setDragError] = useState("");
    const [showError, setShowError] = useState(false);
    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice);
    const IsDarkMode: boolean = useSelector(selector.isDarkMode);
    const navigate = useNavigate();

    const [stepData, setStepData] = useState<StepData>({
        recipientEmails: [],
        newFileID: "",
        selectedFile: null,
        uploadedFileData: null,
        loadedRecipients: [],
        messageText: "",
        reminderDays: 3,
        passwordProtect: false,
        password: "",
        completeInOrder: false
    });

    const [recipientEmails, setRecipientEmails] = useState<RecipientFormData[]>([]);
    const [newFileID, setNewFileID] = useState<string>("");
    const [pdfName, setPdfName] = useState<string | null>(null);
    const [pdfType, setPdfType] = useState<string | null>(null);
    const [pdfBase64, setPdfBase64] = useState<string | null>(null);

    const steps = [
        { id: 1, title: "Upload document", completed: props.isUpdateMode || selectedFile !== null || stepData.selectedFile !== null },
        { id: 2, title: props.isUpdateMode ? 'Edit recipients' : 'Add recipients', completed: stepData.recipientEmails.length > 0 },
        { id: 3, title: "Configure fields", completed: false },
    ];

    useEffect(() => {
        if (props.isUpdateMode && props.fileID) {

            if (pdfName && pdfType && pdfBase64) {
                const fileData = {
                    name: pdfName,
                    type: pdfType,
                    base64: pdfBase64,
                };

                setStepData(prev => ({
                    ...prev,
                    uploadedFileData: fileData
                }));
            }
        }
    }, [props.isUpdateMode, props.fileID, pdfName, pdfType, pdfBase64]);

    useEffect(() => {
        setRecipientEmails(stepData.recipientEmails);
        setNewFileID(stepData.newFileID);
        setSelectedFile(stepData.selectedFile);
    }, [stepData]);

    useEffect(() => {
        if (!props.isUpdateMode && recipientEmails.length > 0 && stepData.recipientEmails.length === 0) {
            setStepData(prev => ({
                ...prev,
                recipientEmails: recipientEmails,
                loadedRecipients: recipientEmails
            }));
        }
    }, [recipientEmails, props.isUpdateMode, stepData.recipientEmails.length]);

    const isStepCompleted = (stepId: number): boolean => {
        switch (stepId) {
            case 1:
                return props.isUpdateMode || stepData.selectedFile !== null || stepData.uploadedFileData !== null;
            case 2:
                return stepData.recipientEmails.length > 0 ||
                    recipientEmails.length > 0 ||
                    stepData.loadedRecipients.length > 0;
            case 3:
                return false;
            default:
                return false;
        }
    };

    const isStepAccessible = (stepId: number): boolean => {
        if (props.isUpdateMode && stepId === 1) return false;
        if (stepId === 1) return true;
        if (stepId === 2) return props.isUpdateMode || isStepCompleted(1);
        if (stepId === 3) return isStepCompleted(1) && isStepCompleted(2);
        return false;
    };

    const handleRecipientsLoad = (loadedRecipients: RecipientFormData[]) => {
        setStepData(prev => ({
            ...prev,
            loadedRecipients: loadedRecipients,
            recipientEmails: loadedRecipients
        }));
    };

    const handleNextStep = (
        emails?: RecipientFormData[],
        fileId?: string,
        formData?: PreservedFormData
    ) => {
        const updatedEmails = emails || stepData.recipientEmails;
        const updatedFileId = fileId || stepData.newFileID;

        setStepData(prev => ({
            ...prev,
            recipientEmails: updatedEmails,
            newFileID: updatedFileId,
            loadedRecipients: updatedEmails,
            messageText: formData?.messageText !== undefined ? formData.messageText : prev.messageText,
            reminderDays: formData?.reminderDays !== undefined ? formData.reminderDays : prev.reminderDays,
            passwordProtect: formData?.passwordProtect !== undefined ? formData.passwordProtect : prev.passwordProtect,
            password: formData?.password !== undefined ? formData.password : prev.password,
            completeInOrder: formData?.completeInOrder !== undefined ? formData.completeInOrder : prev.completeInOrder,
        }));

        const stepComplete = activeStep === 2
            ? updatedEmails.length > 0
            : isStepCompleted(activeStep);

        if (stepComplete && activeStep < 3) {
            setActiveStep(activeStep + 1);
        } else if (activeStep === 2 && updatedEmails.length === 0) {
            setToast("Please add at least one recipient before proceeding", ToasterType.WARNING);
        }
    };

    const handleStepClick = (stepId: number) => {
        if (isStepAccessible(stepId)) {
            setActiveStep(stepId);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
        if (showError) {
            setShowError(false);
            setDragError("");
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
        setShowError(false);
        setDragError("");

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];

            const isPDF = file.type === "application/pdf" ||
                file.name.toLowerCase().endsWith(".pdf");
            const maxSize = 50 * 1024 * 1024;
            const isValidSize = file.size <= maxSize;

            if (!isPDF) {
                setDragError("Only PDF files are allowed. Please select a PDF document.");
                setShowError(true);
                return;
            }

            if (!isValidSize) {
                setDragError(`File size must be under 50MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`);
                setShowError(true);
                return;
            }

            setUploading(true);

            const reader = new FileReader();
            reader.onload = () => {
                const base64 = (reader.result as string).split(",")[1];
                const fileData = {
                    name: file.name,
                    type: file.type,
                    base64: base64,
                };
                setPdfName(file.name);
                setPdfType(file.type);
                setPdfBase64(base64);

                setTimeout(() => {
                    setStepData(prev => ({
                        ...prev,
                        selectedFile: file,
                        uploadedFileData: fileData
                    }));
                    setUploading(false);
                }, 1500);
            };

            reader.onerror = () => {
                setUploading(false);
                setDragError("Failed to read the file. Please try again.");
                setShowError(true);
            };

            reader.readAsDataURL(file);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        setShowError(false);
        setDragError("");

        if (files && files.length > 0) {
            const file = files[0];

            const isPDF = file.type === "application/pdf" ||
                file.name.toLowerCase().endsWith(".pdf");
            const maxSize = 50 * 1024 * 1024;
            const isValidSize = file.size <= maxSize;

            if (!isPDF) {
                setDragError("Only PDF files are allowed. Please select a PDF document.");
                setShowError(true);
                e.target.value = "";
                return;
            }

            if (!isValidSize) {
                setDragError(`File size must be under 50MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`);
                setShowError(true);
                e.target.value = "";
                return;
            }

            setUploading(true);
            const reader = new FileReader();
            reader.onload = () => {
                const base64 = (reader.result as string).split(",")[1];
                const fileData = {
                    name: file.name,
                    type: file.type,
                    base64: base64,
                };
                setPdfName(file.name);
                setPdfType(file.type);
                setPdfBase64(base64);

                setTimeout(() => {
                    setStepData(prev => ({
                        ...prev,
                        selectedFile: file,
                        uploadedFileData: fileData
                    }));
                    setUploading(false);
                }, 1500);
            };

            reader.onerror = () => {
                setUploading(false);
                setDragError("Failed to read the file. Please try again.");
                setShowError(true);
            };

            reader.readAsDataURL(file);
        }
    };

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    const handleRemoveFile = () => {
        setStepData(prev => ({
            ...prev,
            selectedFile: null,
            uploadedFileData: null
        }));
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    return (
        <div className="mx-auto ">
            <div className={`d-flex justify-between align-center ${IsMobileDevice ? 'pt-2' : ''}`}>
                <div className="d-flex align-center">
                    {IsMobileDevice && (
                        <IconButton
                            aria-label="Go back page"
                        >
                            <FalkonDocMobileBackIcon className={`c-pointer`} onClick={() => { navigate(AppRoutes.FALKON_DOCS) }} />
                        </IconButton>
                    )}
                    {steps.map((step, index) => (
                        <div
                            key={step.id}
                            className="d-flex align-center"
                        >
                            <button
                                onClick={() => handleStepClick(step.id)}
                                disabled={!isStepAccessible(step.id)}
                                className={`d-flex ${IsMobileDevice ? 'flex-column align-center step-button-mobile' : 'align-center justify-between'} ${props.isUpdateMode && step.id === 1 ? 'disabled' : ''}`}
                                style={{
                                    cursor: !isStepAccessible(step.id) ? 'not-allowed' : 'pointer',
                                    opacity: !isStepAccessible(step.id) ? 0.5 : 1,
                                    gap: IsMobileDevice ? '1px' : '8px'
                                }}
                            >
                                <div
                                    className={`d-flex align-center justify-center ${activeStep === step.id
                                        ? "secure-doc-step-circle secure-doc-step-circle-active"
                                        : isStepCompleted(step.id)
                                            ? "secure-doc-step-circle secure-doc-step-circle-completed"
                                            : isStepAccessible(step.id)
                                                ? "secure-doc-step-circle secure-doc-step-circle-inactive"
                                                : "secure-doc-step-circle secure-doc-step-circle-inactive"
                                        }`}
                                >
                                    {step.id}
                                </div>
                                <span
                                    className={`h4-semibold step-label ${activeStep === step.id
                                        ? `secure-doc-step-title-active color-primary-accent step-title-underline`
                                        : isStepCompleted(step.id)
                                            ? "secure-doc-step-title-active"
                                            : "secure-doc-step-title-inactive"
                                        } ${IsMobileDevice && activeStep !== step.id ? 'mobile-hide-label' : ''}`}
                                    style={IsMobileDevice ? {
                                        textAlign: 'center',
                                        lineHeight: "17px"
                                    } : {}}
                                >
                                    {step.title}
                                </span>
                            </button>
                            {index < steps.length - 1 && (
                                <div
                                    className={`step-connector ${IsMobileDevice ? 'w-2 h-px mx-2' : 'w-4 h-px ml-2'} ${isStepCompleted(step.id) ? "bg-green-300" : "bg-gray-300"
                                        }`}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>
            <div className={`w-fill  ${IsMobileDevice ? 'd-flex flex-direction' : ` py-5 px-5 ${IsDarkMode ? "" : "bg-color-import-contact-background"}`}`} style={
                IsMobileDevice ? {
                    minHeight: 'calc(100vh - 100px)',
                } : {}
            }>
                {activeStep === 1 && !props.isUpdateMode && (
                    <div className={`${IsDarkMode ? "" : "bg-color-secondary"} w-fill p-4 custom-border-box-size d-flex flex-direction ${IsMobileDevice ? 'flex-1' : 'container-height'
                        }`}>
                        <div
                            className={`d-flex flex-direction justify-center flex-1 w-inherit custom-border-box-size  ${stepData.selectedFile || stepData.uploadedFileData
                                ? ""
                                : isDragOver
                                    ? "upload-box-drag color-primary-dark"
                                    : showError
                                        ? "upload-box-error color-primary-dark"
                                        : `upload-box ${IsDarkMode ? "bg-color-sidebar-bg" : "bg-color-secondary"}`
                                }`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            style={IsMobileDevice ? {
                                minHeight: 'auto',
                                height: 'auto'
                            } : {}
                            }
                        >
                            <div className="d-flex flex-direction ">
                                <div className=" mb-1  t-align-center">
                                    {uploading ? (
                                        <div className="spinner" />
                                    ) : (stepData.selectedFile || stepData.uploadedFileData) ? (
                                        <FalkonDocUploadSuccessIcon strokeWidth={1} />
                                    ) : (
                                        <UploadDocsIcon className="w-20" strokeWidth={1} />
                                    )}
                                </div>

                                <div className="mb-2">
                                    <h3 className={`t-align-center secure-doc-upload-text1 ${isDragOver || showError ? "color-primary-dark" : "color-primary-accent"}`}
                                    >
                                        {uploading
                                            ? "Uploading file..."
                                            : (stepData.selectedFile || stepData.uploadedFileData)
                                                ? "File uploaded successfully"
                                                : showError
                                                    ? "Upload Error"
                                                    : "Choose a file or drag & drop it here"}
                                    </h3>
                                    <p className="t-align-center secure-doc-upload-text2 color-primary-inactive">
                                        {showError ? dragError : "PDF Documents, up to 50MB"}
                                    </p>
                                </div>

                                <ButtonComponent
                                    OnClick={(stepData.selectedFile || stepData.uploadedFileData) ? handleNextStep : handleBrowseClick}
                                    ButtonLabel={(stepData.selectedFile || stepData.uploadedFileData) ? "Next Step" : "Browse File"}
                                    ClassName={`px-4 mx-auto`}
                                    Variant={`${(stepData.selectedFile || stepData.uploadedFileData || isDragOver || showError)
                                        ? "primary-btn-small"
                                        : "secondary-btn-small"
                                        }`}

                                />

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".pdf"
                                    onChange={handleFileSelect}
                                    className="hidden-input"
                                />

                                {(stepData.selectedFile || stepData.uploadedFileData) && (
                                    <div className="selected-file-container pt-4 mt-auto d-flex justify-center mx-auto">
                                        <div className="selected-file-item bg-color-primary-background-pink d-flex align-center justify-between">
                                            <div className="file-info">
                                                <div className="file-details d-flex">
                                                    <span className="file-name color-primary-accent h5-medium d-inline-block w-space o-hidden">
                                                        {stepData.selectedFile?.name || stepData.uploadedFileData?.name}
                                                    </span>
                                                    <span className="color-primary-accent h5-medium">
                                                        {stepData.selectedFile && `(${(stepData.selectedFile.size / 1024 / 1024).toFixed(2)} MB)`}
                                                    </span>
                                                </div>
                                            </div>
                                            <button onClick={handleRemoveFile} className="remove-file-btn bg-color-secondary color-primary d-flex align-center justify-center h3-bold" title="Remove file">×</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {activeStep === 2 && (
                    <div className={IsMobileDevice ? 'flex-1' : ''}>
                        <AddRecipientComponent
                            nextHandler={handleNextStep}
                            isUpdateMode={props.isUpdateMode}
                            fileID={props.fileID}
                            preservedRecipients={
                                stepData.recipientEmails.length > 0
                                    ? stepData.recipientEmails
                                    : stepData.loadedRecipients.length > 0
                                        ? stepData.loadedRecipients
                                        : recipientEmails.length > 0
                                            ? recipientEmails
                                            : []
                            }
                            preservedFormData={{
                                messageText: stepData.messageText,
                                reminderDays: stepData.reminderDays,
                                passwordProtect: stepData.passwordProtect,
                                password: stepData.password,
                                completeInOrder: stepData.completeInOrder
                            }}
                            pdfName={pdfName}
                            pdfType={pdfType}
                            pdfBase64={pdfBase64}
                        />
                    </div>
                )}

                {activeStep === 3 && (
                    <div className={IsMobileDevice ? 'flex-1' : ''}>
                        <DragAndDropComponent
                            recipientEmails={stepData.recipientEmails}
                            newFileID={stepData.newFileID}
                            isUpdateMode={props.isUpdateMode}
                            fileID={props.fileID}
                            onRecipientsLoad={handleRecipientsLoad}
                            pdfBase64={pdfBase64}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default StepperUpload;